import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import { XMLBuilder, XMLParser } from 'fast-xml-parser'

import * as common from '../common'
import * as dms from '../dms'

type ArchiveBase = common.Identified<Archive.Id> &
  common.Named &
  common.Described.Optionally

export type Archive = ArchiveBase & {
  /**
   * The date when this archive event occurred or was last modified
   */
  date: dms.data.expression.instance.definitions.DmsDate
  /**
   * Employee IDs involved in or affected by this archive event
   *
   * @default []
   */
  employees: readonly dms.data.Record.Id[]
  /**
   * Optional instructions or notes about this archive event
   *
   * @default undefined
   */
  instructions?: string
}
export namespace Archive {
  export type Id = string
  export namespace Id {
    export const isId = TypeChecker.isString
  }
  export const isArchive: TypeChecker<Archive> =
    TypeChecker.Factory.intersection(
      common.Identified.getTypeChecker(Archive.Id.isId),
      common.Named.isNamed,
      TypeChecker.Factory.optional(common.Described.isDescribed),
      TypeChecker.Factory.object<Omit<Archive, keyof ArchiveBase>>({
        date: TypeChecker.Factory.instance(
          dms.data.expression.instance.definitions.DmsDate,
        ),
        employees: TypeChecker.Factory.list(dms.data.Record.Id.isId),
        instructions: TypeChecker.Factory.optional(TypeChecker.isString),
      }),
    )
  export const assertIsArchive: TypeAsserter<Archive> =
    TypeAsserter.Factory.from(isArchive)

  /** */
  /**
   * Encodes {@link Data} as XML
   *
   * @see {@link fromXml}, {@link toJson}
   */
  export function toXml(
    data: Archive,
    { format = false }: EncodingOptions = {},
  ): string {
    const xmlBuilder = new XMLBuilder({ format })
    return xmlBuilder.build(data)
  }
  /**
   * Decodes XML to {@link Data}
   *
   * @see {@link toXml}, {@link fromJson}
   */
  export function fromXml(xml: string): Archive {
    const xmlParser = new XMLParser()
    return xmlParser.parse(xml)
  }

  export function representForLlm(archive: Archive): string {
    return `Archive Details:
      - Name: ${archive.name}
      - Description: ${archive.description ?? 'N/A'}
      - Instructions: ${archive.instructions ?? 'N/A'}`
  }

  export interface EncodingOptions {
    /**
     * Set to `true` to make the results more readable
     *
     * @default false
     */
    format?: boolean
  }
}
